import React from "react";
import { isLinkSession } from "./auth.js";
import { Shell, useIsMobile } from "./shell.jsx";
import { HomeView } from "./home.jsx";
import { StatsView } from "./stats.jsx";
import { StableView } from "./stable.jsx";
import { HorseProfile } from "./profile.jsx";
import { LiveView } from "./live.jsx";
import { InspectView } from "./inspect.jsx";
import { TVView } from "./tv.jsx";
import { AdminView } from "./admin.jsx";
import { AccountProfile } from "./account_profile.jsx";
import { InglisAdminView } from "./inglis_admin.jsx";
import { StableTab } from "./stable_tab.jsx";
import { StableHorse } from "./stable_horse.jsx";
import { Inspections } from "./inspections.jsx";
import { Reports } from "./reports.jsx";
import { NotificationPreferencePrompt } from "./notification_preferences.jsx";
import { FeedbackWidget } from "./feedback.jsx";

const { useState } = React;

function App() {
  const sections = window.VD.sections || ["home"];
  const requested = new URLSearchParams(location.search).get("tab");
  // Account Profile is available to every signed-in member, including someone
  // who has no vendor section assigned in the selected realm yet.
  //
  // A staff access link is not a member: it has no account to hold preferences
  // on, so Profile is neither reachable from the URL nor the fallback, and no
  // tab outside the sections the host granted this link is either. The host
  // refuses those reads regardless; this is about not routing a link holder to
  // a screen written for a signed-in identity.
  const link = isLinkSession();
  const reachable = sections.includes(requested) || (!link && requested === "account");
  const initTab = reachable ? requested : (sections[0] || (link ? "home" : "account"));
  const [route, setRoute] = useState(initTab);
  const [horseId, setHorseId] = useState(null);
  const [preselect, setPreselect] = useState(null);
  const [reportLots, setReportLots] = useState([]);
  const [stableMode, setStableMode] = useState("draft");
  const [feedback, setFeedback] = useState(false);
  const mobile = useIsMobile();

  const go = (target) => {
    if (target.startsWith("report:") && sections.includes("reports")) { setReportLots([target.slice(7)]); setRoute("reports"); }
    else if (target.startsWith("horse:")) { setHorseId(target.slice(6)); setRoute("horse"); }
    else if (target.startsWith("inspect:")) { setPreselect(target.slice(8)); setRoute("inspect"); }
    else if (target === "boxes") { setStableMode("boxes"); setRoute("stable"); }
    else { setRoute(target); if (target === "inspect") setPreselect(null); if (target === "stable") setStableMode("draft"); }
  };

  const viewId = (route === "horse") ? "stable" : route;
  const draft = window.DATA.HORSES.filter(h => !h.withdrawn);
  const titles = {
    home:    { eyebrow: `Vendor Dashboard · ${window.VD.home?.sale || window.DATA.SALE.code || ""}`, title: "Today at Riverside" },
    stats:   { eyebrow: `Repository viewing statistics · ${window.VD.repositoryStats.saleId || ""}`, title: "Vendor Stats" },
    stable:  { eyebrow: window.VD.boxAssignments?.enabled ? `Your horses · ${window.VD.boxAssignments.sale}` : `Your draft · ${draft.length} lots`, title: "Stable" },
    horse:   window.VD.boxAssignments?.enabled
      ? { eyebrow: `Your horses · ${window.VD.boxAssignments.sale}`, title: "Horse" }
      : (() => { const h = window.DATA.HORSES.find(x => x.id === horseId);
          return { eyebrow: `Stable · ${h?.sex || ""}`, title: `Lot ${h?.lot || ""}` }; })(),
    live:    { eyebrow: "Camera streams",    title: "Live" },
    inspect: { eyebrow: "Inspection activity", title: "Inspections" },
    reports: { eyebrow: "Your lots and contacts", title: "Reports" },
    tv:      { eyebrow: "TV control",        title: "Televisions" },
    admin:   { eyebrow: "Access management", title: "People" },
    account: { eyebrow: "Your account", title: "Profile" },
    "inglis-admin": { eyebrow: "Inglis operations", title: "Administration" },
  };
  const meta = typeof titles[route] === "function" ? titles[route]() : (titles[route] || titles.home);

  // The feedback pill is a sibling of the shell, not a child of it: the shell's
  // scroll region is an overflow container, and a fixed element inside one is
  // one `transform` away from being clipped to it. At the shell's compact
  // widths there is no pill; its menu opens the same dialog.
  return (
    <>
    <Shell view={viewId} setView={(id) => { setHorseId(null); setRoute(id); }}
           title={meta.title} eyebrow={meta.eyebrow}
           onBack={route === "horse" ? () => setRoute("stable") : null}
           onFeedback={() => setFeedback(true)}>
      {window.VD.access?.realm && window.VD.access.realm !== "inglis"
        && <NotificationPreferencePrompt vendor={window.VD.access.realm}/>}
      {route === "home"    && <HomeView key={`${window.VD.access.realm}:${window.VD.home?.sale}`} go={go}/>}
      {route === "stats"   && <StatsView/>}
      {route === "stable"  && (window.VD.boxAssignments?.enabled
        ? <StableTab key={`${window.VD.access.realm}:${window.VD.boxAssignments.sale}`}
            sale={window.VD.boxAssignments.sale} go={go}
            initialView={stableMode === "boxes" ? "boxes" : "horses"}/>
        : <StableView go={go} initialMode={stableMode}/>)}
      {route === "horse"   && (window.VD.boxAssignments?.enabled
        ? <StableHorse key={horseId} sale={window.VD.boxAssignments.sale} lotId={horseId}/>
        : <HorseProfile horseId={horseId} go={go}/>)}
      {route === "live"    && <LiveView/>}
      {/* The governed panel when the realm has the grants and the section is
          configured; otherwise the prototype, which writes nothing. */}
      {route === "inspect" && (window.VD.inspections?.enabled
        ? <Inspections sale={window.VD.inspections.sale}/>
        : <InspectView go={go} preselectId={preselect}/>)}
      {route === "tv"      && <TVView/>}
      {route === "reports" && window.VD.reports?.enabled && <Reports sale={window.VD.reports.sale} initialLots={reportLots} go={go}/>}
      {route === "admin"   && <AdminView/>}
      {route === "account" && <AccountProfile/>}
      {route === "inglis-admin" && <InglisAdminView/>}
    </Shell>
    <FeedbackWidget open={feedback} onOpenChange={setFeedback} pill={!mobile}/>
    </>
  );
}

export { App };
